import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Link2, Mail, Send, Share2 } from 'lucide-react';
import { api } from '../lib/api';
import { shareSnippet, type ShareLink } from '@grove/api-client';
import { isEmail, parseRecipients, socialTargets } from '../lib/socialShare';
import { Button } from './Button';
import { Modal } from './Modal';

export interface ShareSubjectArticle {
  id: string;
  title: string;
  author?: string | null;
  siteName?: string | null;
  image?: string | null;
  wordCount?: number | null;
}

interface ShareDialogProps {
  article: ShareSubjectArticle;
  open: boolean;
  onClose: () => void;
}

/**
 * The one place Grove hands out a link to an article.
 *
 * Opening the dialog mints (or reuses) the article's public share link, so
 * every path out of here — copy, email, social, OS share sheet — carries
 * grovereader.com/s/<token> rather than the publisher's URL. That page is the
 * top of the funnel; a raw source link would leak the whole growth channel.
 */
export function ShareDialog({ article, open, onClose }: ShareDialogProps) {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sentTo, setSentTo] = useState<number | null>(null);

  const link = useMutation<ShareLink>({
    mutationFn: () => api.createShareLink(article.id),
  });

  const sendEmail = useMutation({
    mutationFn: (to: string[]) => api.shareByEmail(article.id, { to, message: message.trim() || undefined }),
    onSuccess: (result) => {
      setSentTo(result.sent);
      setRecipients('');
      setMessage('');
      // Share counts show up on article cards — refresh them.
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });

  // Mint the link as the dialog opens: everything in here needs it, and the
  // call is idempotent so re-opening never churns tokens.
  useEffect(() => {
    if (!open) return;
    setCopied(false);
    setSentTo(null);
    setEmailError('');
    link.mutate();
    // `link` is a stable mutation object; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, article.id]);

  const shareUrl = link.data?.url ?? '';
  const subject = {
    title: article.title,
    author: article.author,
    siteName: article.siteName,
    url: shareUrl,
  };

  const copy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context / no gesture) — the input below
      // still holds the URL for a manual copy.
    }
  };

  const nativeShare = async () => {
    if (!shareUrl || !navigator.share) return;
    try {
      await navigator.share({ title: article.title, text: shareSnippet(subject), url: shareUrl });
    } catch {
      // Sheet dismissed — nothing to do.
    }
  };

  const submitEmail = (e: React.FormEvent) => {
    e.preventDefault();
    const to = parseRecipients(recipients);
    if (!to.length) return setEmailError('Add at least one email address.');
    const bad = to.filter((address) => !isEmail(address));
    if (bad.length) return setEmailError(`Not a valid email: ${bad.join(', ')}`);
    if (to.length > 5) return setEmailError('You can share with up to 5 people at a time.');
    setEmailError('');
    sendEmail.mutate(to);
  };

  const byline = [article.author && `By ${article.author}`, article.siteName]
    .filter(Boolean)
    .join(' · ');

  return (
    <Modal open={open} onClose={onClose} title="Share this article">
      {/* Preview — the same card the recipient sees in their inbox. */}
      <div className="flex gap-3 mb-5 p-3 rounded-lg border border-white/10 bg-white/[0.03]">
        {article.image && (
          <img
            src={article.image}
            alt=""
            className="w-14 h-14 rounded-md object-cover flex-shrink-0"
          />
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-white line-clamp-2">{article.title}</p>
          {byline && <p className="text-xs text-text-secondary mt-1 truncate">{byline}</p>}
        </div>
      </div>

      {/* Public link */}
      <label className="flex items-center gap-1.5 text-xs font-medium text-text-secondary mb-1.5">
        <Link2 size={13} /> Public Grove link
      </label>
      <div className="flex gap-2 mb-2">
        <input
          readOnly
          value={link.isPending ? 'Creating link…' : link.isError ? 'Could not create link' : shareUrl}
          onFocus={(e) => e.currentTarget.select()}
          className="flex-1 px-3 py-2 rounded-lg text-sm text-text-primary bg-surface-0 border border-border-subtle focus:border-prism-violet focus:outline-none font-mono"
        />
        <Button variant="secondary" onClick={copy} disabled={!shareUrl}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="text-xs text-text-secondary mb-5">
        Anyone with this link can read the article on Grove — no account needed.
      </p>

      {/* Social + OS share sheet */}
      <div className="flex flex-wrap gap-2 mb-6">
        {socialTargets(subject).map((target) => (
          <a
            key={target.id}
            href={shareUrl ? target.href : undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!shareUrl}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border border-white/20 text-white transition-colors ${
              shareUrl ? 'hover:border-white/40' : 'opacity-50 pointer-events-none'
            }`}
          >
            {target.label}
          </a>
        ))}
        {typeof navigator !== 'undefined' && 'share' in navigator && (
          <Button variant="secondary" size="sm" onClick={nativeShare} disabled={!shareUrl}>
            <Share2 size={14} /> More…
          </Button>
        )}
      </div>

      {/* Email */}
      <form onSubmit={submitEmail} className="border-t border-white/10 pt-5">
        <label className="flex items-center gap-1.5 text-xs font-medium text-text-secondary mb-1.5">
          <Mail size={13} /> Send it by email
        </label>
        <input
          type="text"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
          placeholder="friend@example.com, someone@else.com"
          className="w-full px-3 py-2 mb-2 rounded-lg text-sm text-text-primary bg-surface-0 border border-border-subtle focus:border-prism-violet focus:outline-none"
        />
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          maxLength={1000}
          placeholder="Add a note (optional)"
          className="w-full px-3 py-2 mb-2 rounded-lg text-sm text-text-primary bg-surface-0 border border-border-subtle focus:border-prism-violet focus:outline-none resize-none"
        />
        {emailError && <p className="text-red-400 text-sm mb-2">{emailError}</p>}
        {sendEmail.isError && (
          <p className="text-red-400 text-sm mb-2">Couldn’t send that — try again in a moment.</p>
        )}
        {sentTo !== null && (
          <p className="text-emerald-400 text-sm mb-2">
            Sent to {sentTo} {sentTo === 1 ? 'person' : 'people'}.
          </p>
        )}
        <Button type="submit" disabled={sendEmail.isPending || !shareUrl} className="w-full">
          <Send size={14} /> {sendEmail.isPending ? 'Sending…' : 'Send'}
        </Button>
      </form>
    </Modal>
  );
}
